import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import api from '../api';
import CustomDataTable from '../CustomDataTable';
import AuthContext from '../../context/AuthProvider';
import { canDo } from '../../utils/canDo';
import CallLogModal from '../CallLogModal';
import KITDueDashboard from './KITDueDashboard';
import KITBulkActivate from './KITBulkActivate';
import WhatsAppConfigModal from '../WhatsAppConfigModal';
import { useWhatsApp } from '../../context/WhatsAppContext';
import './KIT.css';

/* Renders any modal overlay into document.body, escaping all parent stacking contexts */
function KitPortal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const leadName = (l) => l.enquiredForName || l.name || 'Unknown';

const kitDay = (lead) => {
  if (!lead.kitActive || !lead.kitStartDate) return null;
  const diff = Math.floor((Date.now() - new Date(lead.kitStartDate)) / (1000 * 60 * 60 * 24));
  return diff + 1;
};

const daysSince = (d) => !d ? 999 : Math.floor((Date.now() - new Date(d)) / 86400000);

const isStale = (lead) => {
  if (lead.status === 'Nil / No Response') return true;
  if (lead.kitActive) return false;
  if (lead.status === 'Appointment Booked') return false;
  const lastContact = lead.lastContactedDate || lead.lastvisitdate;
  if (!lastContact) return true;
  return daysSince(lastContact) >= 7;
};

const avatarColor = (name = '') => {
  const colors = ['#4f46e5', '#0891b2', '#7c3aed', '#d97706', '#dc2626', '#16a34a', '#0284c7', '#9333ea'];
  return colors[(name.charCodeAt(0) || 0) % colors.length];
};

const initials = (name = '') =>
  name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  const today = new Date();
  if (dt.toDateString() === today.toDateString()) return 'Today';
  const diff = daysSince(d);
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff}d ago`;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusColor = (s = '') => {
  if (s === 'Appointment Booked') return '#16a34a';
  if (s === 'Will Decide') return '#d97706';
  if (s === 'Nil / No Response') return '#ef4444';
  if (s === 'KIT Active') return '#0d9488';
  return '#374151';
};

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const AILMENTS = [
  'Lower Back Pain', 'Cervical/Neck Pain', 'Sciatica', 'Knee Pain', 'Shoulder Pain',
  'Upper Back Pain', 'Hip Pain', 'Wrist/Hand Pain', 'Ankle/Foot Pain',
  'Headache/Migraine', 'Arm Pain', 'Thigh/Calf Pain', 'Chest Pain', 'Digestive Issues',
];
const SOURCES = ['Google', 'MYC Website', 'KnownPerson', 'Referral', 'YouTube', 'Instagram', 'Other'];
const STATUS_OPTIONS = ['Appointment Booked', 'Will Decide', 'Nil / No Response'];
const TEMP_OPTIONS = ['Hot (Serious/Urgent)', 'Warm (Interested)', 'Cold (Just Enquiry)'];
const DURATION_OPTIONS = ['Less than 1 week', '1-4 weeks', '1-6 months', 'More than 6 months'];
const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'];
const REPORT_OPTIONS = ['MRI', 'X-Ray', 'None'];

/* ─────────────────────────────────────────
   WHATSAPP TEMPLATES (shared with Callleads)
───────────────────────────────────────── */
const WA_TEMPLATES = [
  { id: 'welcome', label: '👋 Welcome Message', type: 'Welcome', text: "welcome to our therapy program! We're here to help you recover and feel better soon. 🌿" },
  { id: 'followup', label: '🔁 Follow-up Reminder', type: 'Follow-up', text: "just checking in! How are you feeling since our last call?" },
  { id: 'lbp1', label: '💡 Lower Back — Small Movements', type: 'Tip / Advice', text: "Small Movements, Big Protection 🩺\nStudies show that lumbar disc pressure rises by up to 40% when sitting for long hours. Just standing or walking for 2 minutes every 30–40 minutes improves blood flow and reduces stiffness by over 60%.\n\nFrom the DS 3-Phase Method of Permanent Recovery™ — Ancient Wisdom. Future Tech. Healing Now." },
  { id: 'lbp2', label: '💧 Lower Back — Hydration', type: 'Tip / Advice', text: "Hydration is Hidden Medicine 💧\nSpinal discs are made of nearly 85% water. Even mild dehydration reduces their cushioning capacity. Sipping warm water steadily through the day keeps discs hydrated, supple, and pain-free.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'lbp3', label: '🌙 Lower Back — 3-Minute Reset', type: 'Tip / Advice', text: "The 3-Minute Reset Before Sleep 🌙\nPeople with chronic back pain hold 15–30% more muscle tension even during rest. Before sleep, lie flat, breathe slowly, and consciously relax your lower back for 3 minutes. This reduces neural tension and promotes natural overnight recovery.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'lbp4', label: '🏥 Lower Back — At MYC Centre', type: 'Authority', text: "At our centre, we don't suppress pain — we decode it and resolve it from the root. 🩺\n\nThe DS 3-Phase Method of Permanent Recovery™ blends medical precision with advanced biomechanics-based healing.\n\nAncient Wisdom. Future Tech. Healing Now." },
  { id: 'lbp5', label: '⚡ Lower Back — Sciatica Protocol', type: 'Authority', text: "In most sciatica cases, pain isn't from the disc alone — it's from nerve compression and pelvic misalignment.\n\nUsing our DS 3-Phase method, we restore natural mobility between vertebrae and nerve pathways. 87% of patients see visible relief within 10 sessions. 🌿\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'success1', label: '🌟 Success Story — Rishabh', type: 'Success Story', text: "🌿 Rishabh's Story — From Pain to Performance\n\n\"I couldn't get out of bed for months. Then I discovered the DS 3-Phase Method at MYC. Today I'm not only pain-free but back to playing cricket!\" — Rishabh, CEO.\n\n🎥 Watch: https://www.youtube.com/watch?v=htyzNmYPoVo\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'neck1', label: '📱 Neck Pain — Screen Angle', type: 'Tip / Advice', text: "Screen Angle Defines Neck Health 📱\n\nTilting your head just 30° forward increases neck load from 5 kg to nearly 18 kg. Keep your screen at eye level to reduce strain on cervical discs.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'neck2', label: '🔄 Neck Pain — Micro Movements', type: 'Tip / Advice', text: "Micro-Movement Rehydrates Cervical Discs 🔄\n\nEvery 45 minutes, perform gentle chin tucks and shoulder rolls. These micro-movements restore joint lubrication and reduce nerve pressure by up to 20%.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'knee1', label: '💪 Knee Pain — Quadriceps', type: 'Tip / Advice', text: "The Quadriceps Connection 💪\n\nWeak quadriceps increase joint load on the knee by up to 45%. Simple strength work like seated leg raises helps absorb shock and protect the joint.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'mattress', label: '🛏️ Mattress Advice', type: 'Lifestyle / Awareness', text: "Choose a medium-firm mattress for better spine support 🛏️\n\nA mattress that's too soft allows the spine to sag; too firm creates pressure points. Medium-firm is the scientific sweet spot for spinal alignment during sleep.\n\nFrom the DS 3-Phase Method of Permanent Recovery™" },
  { id: 'consult', label: '📅 Consultation Reminder', type: 'Reminder', text: "It's always best to consult early 📅\n\nDelaying treatment allows conditions to worsen. A detailed spine/joint checkup now can prevent complications later. Would you like to book a consultation this week?\n\nAt MYC Centre — Ancient Wisdom. Future Tech. Healing Now." },
  { id: 'custom', label: '✏️ Custom Message', type: 'Custom', text: '' },
];

/* ─────────────────────────────────────────
   CAMPAIGN STEPS PRESETS
───────────────────────────────────────── */
const CAMPAIGN_PRESETS = {
  'Lower Back Pain': [
    { day: 2,  type: 'Tip / Advice',        icon: '💡', title: 'Back Pain Tip',         text: 'Avoid sitting continuously. Follow the DS 30–30 rule. Sit 30 minutes → Move for 30 seconds.', delay: null },
    { day: 5,  type: 'Lifestyle / Awareness',icon: '🛏️', title: 'Mattress Advice',       text: 'Choose a medium-firm mattress for better spine support.', delay: 'After 2 Days' },
    { day: 10, type: 'Success Story',        icon: '🔄', title: 'Success Story',         text: 'A patient avoided surgery after targeted spine strengthening therapy.', delay: 'After 3 Days' },
    { day: 20, type: 'Authority',            icon: '📍', title: 'Spine Awareness',       text: 'Learn how poor posture affects spine-health & can worsen back issues.', delay: 'After 5 Days' },
    { day: 30, type: 'Reminder',             icon: '📅', title: 'Consultation Reminder', text: "It's best to consult early for a detailed spine checkup. Discuss options.", delay: 'After 5 Days' },
  ],
  'Cervical/Neck Pain': [
    { day: 2,  type: 'Tip / Advice',        icon: '📱', title: 'Screen Posture Tip',    text: 'Keep your screen at eye level. Every 30° forward tilt adds 13 kg of neck load.', delay: null },
    { day: 5,  type: 'Tip / Advice',        icon: '🔄', title: 'Micro Movements',       text: 'Every 45 minutes, do gentle chin tucks to rehydrate cervical discs.', delay: 'After 2 Days' },
    { day: 10, type: 'Lifestyle / Awareness',icon: '🛏️', title: 'Sleep Support',        text: 'A low, firm pillow aligned with the neck curve reduces morning stiffness by 50%.', delay: 'After 3 Days' },
    { day: 20, type: 'Authority',            icon: '🏥', title: 'DS Method',             text: 'Our DS 3-Phase Method restores natural cervical alignment without machines.', delay: 'After 5 Days' },
    { day: 30, type: 'Reminder',             icon: '📅', title: 'Checkup Reminder',      text: 'Would you like to book a cervical spine evaluation this week?', delay: 'After 5 Days' },
  ],
  'Knee Pain': [
    { day: 2,  type: 'Tip / Advice',        icon: '💪', title: 'Quadriceps Tip',        text: 'Weak quadriceps increase knee load by 45%. Try seated leg raises daily.', delay: null },
    { day: 5,  type: 'Tip / Advice',        icon: '⚖️', title: 'Weight & Pressure',     text: 'Every extra kg of body weight adds 4 kg of pressure on each knee.', delay: 'After 2 Days' },
    { day: 10, type: 'Authority',            icon: '📐', title: 'Alignment First',       text: 'Most knee pain stems from hip and ankle misalignment, not the knee itself.', delay: 'After 3 Days' },
    { day: 20, type: 'Success Story',        icon: '🌟', title: 'Recovery Story',        text: 'Patient walked pain-free within 12 sessions using DS 3-Phase Method.', delay: 'After 5 Days' },
    { day: 30, type: 'Reminder',             icon: '📅', title: 'Consultation Prompt',   text: "Don't wait for pain to worsen. Early intervention gives the best results.", delay: 'After 5 Days' },
  ],
  'Sciatica': [
    { day: 2,  type: 'Tip / Advice',        icon: '⚡', title: 'Sciatica Basics',       text: 'Sciatica pain usually comes from nerve compression — not always from the disc alone.', delay: null },
    { day: 5,  type: 'Lifestyle / Awareness',icon: '🪑', title: 'Sitting Posture',       text: 'Avoid crossing legs or sitting on soft surfaces for long periods.', delay: 'After 2 Days' },
    { day: 10, type: 'Authority',            icon: '🏥', title: 'DS Protocol',           text: '87% of sciatica patients see visible relief within 10 sessions at MYC.', delay: 'After 3 Days' },
    { day: 20, type: 'Success Story',        icon: '🌟', title: 'Real Recovery',         text: 'Patient with 100% nerve block walked normally in 3 weeks with DS Method.', delay: 'After 5 Days' },
    { day: 30, type: 'Reminder',             icon: '📅', title: 'Evaluation Reminder',   text: 'A proper nerve pathway assessment can pinpoint your exact pain source.', delay: 'After 5 Days' },
  ],
};

/* ─────────────────────────────────────────
   CAMPAIGN MATCHING — maps ailments → preset
───────────────────────────────────────── */
function matchCampaignPreset(lead) {
  const combined = [
    ...(lead.ailments || []),
    lead.enquiryDetails || '',
    lead.comments || '',
    lead.otherAilment || '',
  ].join(' ').toLowerCase();
  if (/sciatica|sciatic/.test(combined)) return 'Sciatica';
  if (/lower.?back|lumbar|lumbago|l4|l5|disc/.test(combined)) return 'Lower Back Pain';
  if (/cervical|neck|spondyl|c5|c6/.test(combined)) return 'Cervical/Neck Pain';
  if (/knee|patell|meniscus|acl|pcl/.test(combined)) return 'Knee Pain';
  return 'Lower Back Pain'; // sensible default
}

/* Build a kitSchedule array from a preset + start date */
function buildKitSchedule(leadId, campaignName, startDate) {
  const preset = CAMPAIGN_PRESETS[campaignName] || CAMPAIGN_PRESETS['Lower Back Pain'];
  return preset.map((step, i) => ({
    scheduleId: `${leadId}_d${step.day}_${i}`,
    day: step.day,
    scheduledDate: new Date(new Date(startDate).getTime() + (step.day - 1) * 86400000).toISOString(),
    type: step.type,
    icon: step.icon,
    title: step.title,
    text: step.text,
    campaignName,
    status: 'pending',
    sentDate: null,
    sentText: null,
  }));
}

const MSG_LIBRARY_TYPES = [
  { icon: '💡', label: 'Tip / Advice',        desc: 'Health & posture tips backed by research' },
  { icon: '🏛️', label: 'Authority',            desc: 'DS Method expertise & credibility builders' },
  { icon: '🌟', label: 'Success Story',        desc: 'Real patient recovery testimonials' },
  { icon: '🌿', label: 'Lifestyle / Awareness',desc: 'Daily habits for better spine health' },
  { icon: '🎥', label: 'Video / Doctor',       desc: 'Video content & doctor messages' },
  { icon: '🔔', label: 'Reminder',             desc: 'Gentle follow-up & consultation nudges' },
  { icon: '🩺', label: 'Consultation Prompt',  desc: 'Direct appointment conversion messages' },
];


/* ─────────────────────────────────────────
   EMPTY FORM
───────────────────────────────────────── */
const emptyForm = () => ({
  name: '',
  number: '',
  email: '',
  address: '',
  relationship: 'self',
  preferredContact: 'phone',
  enquiredForName: '',
  enquiredForAge: '',
  enquiredForGender: 'Male',
  enquiredForNumber: '',
  city: '',
  ailments: [],
  otherAilment: '',
  painDuration: '',
  painSeverity: '',
  reportsAvailable: [],
  status: 'Will Decide',
  enquirySource: '',
  instagramHandle: '',
  otherSource: '',
  leadTemperature: 'Warm (Interested)',
  comments: '',
  enquiryDate: new Date().toISOString().split('T')[0],
  enquiryDetails: '',
  referrerName: '',
  followUpFrequency: 7,
  contactedPerson: '',
  followUpDate: '',
  lastContactedDate: '',
});

/* ─────────────────────────────────────────
   SHARED AVATAR CELL
───────────────────────────────────────── */
function AvatarCell({ lead, onClick }) {
  const name = leadName(lead);
  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: onClick ? 'pointer' : 'default' }}
    >
      <span style={{
        width: 32, height: 32, borderRadius: '50%',
        background: avatarColor(name),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0,
        boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
      }}>
        {initials(name)}
      </span>
      <span style={{ fontWeight: 500, fontSize: 13 }}>{name}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN KIT COMPONENT
═══════════════════════════════════════════ */
export default function KIT() {
  const { auth } = useContext(AuthContext);
  const canDelete = canDo(auth, 'leads.delete');
  const { config: waConfig, setConfigOpen } = useWhatsApp();

  const [view, setView] = useState('intelligence');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeChips, setActiveChips] = useState([]);
  const [activeSources, setActiveSources] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [kitLogModal, setKitLogModal] = useState(null); // { lead, mode: 'call'|'whatsapp' }
  const [showBulkModal, setShowBulkModal] = useState(false);
  // personType removed — KIT now only shows Calllead records (not enrolled patients)
  const [staffList, setStaffList] = useState([]);

  /* ── Fetch leads ── */
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${process.env.REACT_APP_URL}/callleads/profiles?t=${Date.now()}`);
      setLeads(res.data || []);
    } catch (err) {
      console.error('KIT fetch error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => {
    api.get(`${process.env.REACT_APP_URL}/users/staff`).then(r => setStaffList(r.data || [])).catch(() => {});
  }, []);

  /* ── Derived counts ── */
  const totalLeads = leads.length;
  const kitActive = leads.filter((l) => l.kitActive).length;
  const appointments = leads.filter((l) => l.status === 'Appointment Booked').length;
  const willDecide = leads.filter((l) => l.status === 'Will Decide').length;
  const nilNoResponse = leads.filter((l) => l.status === 'Nil / No Response').length;
  const hotCount = leads.filter((l) => (l.leadTemperature || '').toLowerCase().includes('hot')).length;
  const staleCount = leads.filter((l) => isStale(l) && l.status !== 'Nil / No Response').length;

  const toggleChip = useCallback((chip) => {
    setActiveChips((prev) => prev.includes(chip) ? prev.filter((c) => c !== chip) : [...prev, chip]);
  }, []);

  const toggleSource = useCallback((src) => {
    setActiveSources((prev) => prev.includes(src) ? prev.filter((s) => s !== src) : [...prev, src]);
  }, []);

  /* ── Filtered leads for table — sorted by lastContactedDate DESC ── */
  const filteredLeads = leads
    .filter((l) => {
      const chipMatch = activeChips.length === 0 || activeChips.some((chip) => {
        if (chip === 'appointments') return l.status === 'Appointment Booked';
        if (chip === 'willDecide') return l.status === 'Will Decide';
        if (chip === 'noResponse') return l.status === 'Nil / No Response';
        if (chip === 'kitActive') return l.kitActive;
        if (chip === 'stale') return isStale(l) && l.status !== 'Nil / No Response';
        if (chip === 'hot') return (l.leadTemperature || '').toLowerCase().includes('hot');
        return false;
      });
      const sourceMatch = activeSources.length === 0 || activeSources.includes(l.enquirySource);
      return chipMatch && sourceMatch;
    })
    .sort((a, b) => {
      const da = a.lastContactedDate ? new Date(a.lastContactedDate).getTime() : 0;
      const db = b.lastContactedDate ? new Date(b.lastContactedDate).getTime() : 0;
      return db - da;
    });

  /* ── Update a single lead ── */
  const updateLead = async (lead, patch) => {
    try {
      await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/update/${lead._id}`, patch);
      setLeads((prev) => prev.map((l) => (l._id === lead._id ? { ...l, ...patch } : l)));
      if (selectedLead && selectedLead._id === lead._id) {
        setSelectedLead((prev) => ({ ...prev, ...patch }));
      }
    } catch (err) {
      console.error('Update error', err);
    }
  };

  /* ── Delete a lead ── */
  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${leadName(lead)}"? This cannot be undone.`)) return;
    try {
      await api.delete(`${process.env.REACT_APP_URL}/callleads/profiles/delete/${lead._id}`);
      setLeads((prev) => prev.filter((l) => l._id !== lead._id));
      if (selectedLead?._id === lead._id) setSelectedLead(null);
    } catch (err) {
      console.error('Delete error', err);
      alert('Failed to delete lead. Please try again.');
    }
  };

  /* ── Open Call Log Modal (call or whatsapp mode) ── */
  const openWhatsApp = (lead) => setKitLogModal({ lead, mode: 'whatsapp' });
  const handleLogCall = (lead) => setKitLogModal({ lead, mode: 'call' });

  /* ── After modal saves — update leads and selectedLead state ── */
  const handleKitLogSaved = (lead, updates) => {
    setLeads((prev) => prev.map((l) => l._id === lead._id ? { ...l, ...updates } : l));
    if (selectedLead?._id === lead._id) setSelectedLead((prev) => ({ ...prev, ...updates }));
  };

  /* ── Start KIT — creates campaign schedule based on lead ailments ── */
  const handleStartKIT = async (lead) => {
    const campaignName = matchCampaignPreset(lead);
    const startDate = new Date().toISOString();
    const kitSchedule = buildKitSchedule(lead._id, campaignName, startDate);
    await updateLead(lead, { kitActive: true, kitStartDate: startDate, kitCampaign: campaignName, kitSchedule });
  };

  /* ── Change campaign for an active KIT lead (rebuilds schedule, keeps sent msgs) ── */
  const handleChangeCampaign = async (lead, campaignName) => {
    const startDate = lead.kitStartDate || new Date().toISOString();
    // Preserve any already-sent messages; rebuild pending ones from new campaign
    const sentItems = (lead.kitSchedule || []).filter(m => m.status === 'sent');
    const newSchedule = buildKitSchedule(lead._id, campaignName, startDate);
    // Merge: keep sent items, use new pending items (skip days already sent)
    const sentDays = new Set(sentItems.map(m => m.day));
    const merged = [
      ...sentItems,
      ...newSchedule.filter(m => !sentDays.has(m.day)),
    ].sort((a, b) => a.day - b.day);
    await updateLead(lead, { kitCampaign: campaignName, kitSchedule: merged });
  };

  /* ── Assign campaign to a lead not yet in KIT ── */
  const handleAssignAndStartKIT = async (lead, campaignName) => {
    const startDate = new Date().toISOString();
    const kitSchedule = buildKitSchedule(lead._id, campaignName, startDate);
    await updateLead(lead, { kitActive: true, kitStartDate: startDate, kitCampaign: campaignName, kitSchedule });
  };

  /* ── Mark a scheduled message as sent ── */
  const handleSendScheduledMessage = async (lead, scheduleId, sentText) => {
    const now = new Date().toISOString();
    const kitSchedule = (lead.kitSchedule || []).map(m =>
      m.scheduleId === scheduleId ? { ...m, status: 'sent', sentDate: now, sentText } : m
    );
    const updates = {
      kitSchedule,
      messageHistory: [...(lead.messageHistory || []), { date: now, type: 'KIT Campaign', text: sentText }],
      lastMessageDate: now,
      lastContactedDate: now,
    };
    await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/update/${lead._id}`, updates);
    setLeads(prev => prev.map(l => l._id === lead._id ? { ...l, ...updates } : l));
    if (selectedLead?._id === lead._id) setSelectedLead(prev => ({ ...prev, ...updates }));
  };

  /* ── Skip a scheduled message ── */
  const handleSkipScheduledMessage = async (lead, scheduleId) => {
    const now = new Date().toISOString();
    const kitSchedule = (lead.kitSchedule || []).map(m =>
      m.scheduleId === scheduleId ? { ...m, status: 'skipped', sentDate: now } : m
    );
    const updates = { kitSchedule };
    await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/update/${lead._id}`, updates);
    setLeads(prev => prev.map(l => l._id === lead._id ? { ...l, ...updates } : l));
    if (selectedLead?._id === lead._id) setSelectedLead(prev => ({ ...prev, ...updates }));
  };

  /* ── Bulk KIT activation (called by KITBulkActivate) ── */
  const handleBulkActivate = async (selectedLeads, campaignOverride, onProgress) => {
    for (let i = 0; i < selectedLeads.length; i++) {
      const lead = selectedLeads[i];
      const campaignName = campaignOverride || matchCampaignPreset(lead);
      const startDate = new Date().toISOString();
      const kitSchedule = buildKitSchedule(lead._id, campaignName, startDate);
      await updateLead(lead, { kitActive: true, kitStartDate: startDate, kitCampaign: campaignName, kitSchedule });
      onProgress && onProgress(i + 1);
    }
  };

  /* ── Bulk custom message send (opens WA for each — queued manually) ── */
  const handleBulkSendCustom = (selectedLeads, messageText) => {
    // Opens first lead's WA — user sends and confirms, then next opens
    // In practice we just queue the message to messageHistory for each lead
    selectedLeads.forEach(async lead => {
      const name = lead.enquiredForName || lead.name || 'Patient';
      const text = `Namaste ${name} Ji! ${messageText}`;
      const now = new Date().toISOString();
      const updates = {
        messageHistory: [...(lead.messageHistory || []), { date: now, type: 'Bulk Campaign', text }],
        lastMessageDate: now,
        lastContactedDate: now,
      };
      await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/update/${lead._id}`, updates);
      setLeads(prev => prev.map(l => l._id === lead._id ? { ...l, ...updates } : l));
    });
  };

  /* ── Open add modal ── */
  const openAddModal = () => {
    setEditingLead(null);
    setForm({ ...emptyForm(), contactedPerson: auth?.username || '' });
    setShowModal(true);
  };

  /* ── Open edit modal ── */
  const openEditModal = (lead) => {
    setEditingLead(lead);
    setForm({
      name: lead.name || '',
      number: lead.number || '',
      email: lead.email || '',
      address: lead.address || lead.city || '',
      relationship: lead.relationship || 'self',
      preferredContact: lead.preferredContact || 'phone',
      enquiredForName: lead.enquiredForName || '',
      enquiredForAge: lead.enquiredForAge || '',
      enquiredForGender: lead.enquiredForGender || 'Male',
      enquiredForNumber: lead.enquiredForNumber || '',
      city: lead.city || '',
      ailments: lead.ailments || [],
      otherAilment: '',
      painDuration: lead.painDuration || '',
      painSeverity: lead.painSeverity || '',
      reportsAvailable: lead.reportsAvailable || [],
      status: lead.status || 'Will Decide',
      enquirySource: lead.enquirySource || '',
      instagramHandle: '',
      otherSource: '',
      leadTemperature: lead.leadTemperature || 'Warm (Interested)',
      comments: lead.comments || '',
      enquiryDate: lead.enquiryDate ? new Date(lead.enquiryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      enquiryDetails: lead.enquiryDetails || '',
      referrerName: lead.referrerName || '',
      followUpFrequency: lead.followUpFrequency || 7,
      contactedPerson: lead.contactedPerson || '',
      followUpDate: lead.followUpDate ? new Date(lead.followUpDate).toISOString().split('T')[0] : '',
      lastContactedDate: lead.lastContactedDate ? new Date(lead.lastContactedDate).toISOString().split('T')[0] : '',
    });
    setShowModal(true);
  };

  /* ── Save lead ── */
  const handleSave = async (convertToAppointment = false) => {
    setSaving(true);
    const payload = {
      name: form.name || form.enquiredForName,
      enquiredForName: form.enquiredForName,
      enquiredForAge: form.enquiredForAge,
      enquiredForGender: form.enquiredForGender,
      enquiredForNumber: form.enquiredForNumber,
      number: form.number,
      email: form.email,
      address: form.address,
      city: form.city || form.address,
      relationship: form.relationship,
      preferredContact: form.preferredContact,
      branch: auth?.branch || 'MedicalYogaTherapyCentre',
      inputBranch: auth?.branch || 'MedicalYogaTherapyCentre',
      enquiryDate: form.enquiryDate || new Date().toISOString(),
      enquiryDetails: form.enquiryDetails || form.comments || '-',
      ailments: [...form.ailments, ...(form.otherAilment ? [form.otherAilment] : [])],
      painDuration: form.painDuration,
      painSeverity: form.painSeverity,
      reportsAvailable: form.reportsAvailable,
      status: convertToAppointment ? 'Appointment Booked' : form.status,
      enquirySource: form.enquirySource,
      leadTemperature: form.leadTemperature,
      comments: form.comments,
      referrerName: form.referrerName,
      followUpFrequency: form.followUpFrequency || 7,
      contactedPerson: form.contactedPerson,
      ...(form.followUpDate ? { followUpDate: new Date(form.followUpDate).toISOString() } : {}),
    };
    try {
      if (editingLead) {
        await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/update/${editingLead._id}`, payload);
      } else {
        await api.post(`${process.env.REACT_APP_URL}/callleads/profiles/add`, payload);
      }
      await fetchLeads();
      setShowModal(false);
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleAilment = (a) =>
    setForm((f) => ({
      ...f,
      ailments: f.ailments.includes(a) ? f.ailments.filter((x) => x !== a) : [...f.ailments, a],
    }));

  const toggleReport = (r) =>
    setForm((f) => ({
      ...f,
      reportsAvailable: f.reportsAvailable.includes(r) ? f.reportsAvailable.filter((x) => x !== r) : [...f.reportsAvailable, r],
    }));

  /* ═══════════════ DERIVED: due / upcoming messages ═══════════════ */
  const now = Date.now();
  const threeDaysFromNow = now + 3 * 86400000;
  const dueMessages = [];
  const upcomingMessages = [];
  leads.forEach(lead => {
    if (!lead.kitActive || !lead.kitSchedule) return;
    lead.kitSchedule.forEach(msg => {
      if (msg.status !== 'pending') return;
      const t = new Date(msg.scheduledDate).getTime();
      if (t <= now) dueMessages.push({ lead, msg });
      else if (t <= threeDaysFromNow) upcomingMessages.push({ lead, msg });
    });
  });
  dueMessages.sort((a, b) => new Date(a.msg.scheduledDate) - new Date(b.msg.scheduledDate));
  upcomingMessages.sort((a, b) => new Date(a.msg.scheduledDate) - new Date(b.msg.scheduledDate));

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="kit-root">
      <div className="kit-topbar">
        <div className="kit-nav-tabs">
          <button
            className={`kit-nav-tab ${view === 'intelligence' ? 'active' : ''}`}
            onClick={() => setView('intelligence')}
          >
            Lead Intelligence
          </button>
          <button
            className={`kit-nav-tab ${view === 'performance' ? 'active' : ''}`}
            onClick={() => setView('performance')}
          >
            KIT Performance
          </button>
          <button
            className={`kit-nav-tab ${view === 'campaigns' ? 'active' : ''}`}
            onClick={() => setView('campaigns')}
          >
            Campaign Builder
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* WhatsApp provider indicator + config button */}
          <button
            onClick={() => setConfigOpen(true)}
            title="Configure WhatsApp provider"
            style={{
              padding: '6px 12px', background: waConfig.provider === 'manual' ? '#f0fdf4' : waConfig.provider === 'wati' ? '#eff6ff' : '#fff0f0',
              border: `1.5px solid ${waConfig.provider === 'manual' ? '#86efac' : waConfig.provider === 'wati' ? '#a5b4fc' : '#fca5a5'}`,
              borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              color: waConfig.provider === 'manual' ? '#15803d' : waConfig.provider === 'wati' ? '#4338ca' : '#dc2626',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            📲 {waConfig.provider === 'manual' ? 'Manual' : waConfig.provider.toUpperCase()}
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            style={{
              padding: '7px 14px', background: '#0d9488', color: '#fff',
              border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, position: 'relative',
            }}
          >
            🚀 Bulk KIT
            {dueMessages.length > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff',
                borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {dueMessages.length}
              </span>
            )}
          </button>
          <button className="kit-btn-primary" onClick={openAddModal}>
            + New Enquiry
          </button>
        </div>
      </div>

      {view === 'intelligence' && (
        <IntelligenceView
          leads={leads}
          loading={loading}
          selectedLead={selectedLead}
          setSelectedLead={setSelectedLead}
          activeChips={activeChips}
          setActiveChips={setActiveChips}
          toggleChip={toggleChip}
          activeSources={activeSources}
          setActiveSources={setActiveSources}
          toggleSource={toggleSource}
          filteredLeads={filteredLeads}
          totalLeads={totalLeads}
          kitActive={kitActive}
          appointments={appointments}
          willDecide={willDecide}
          nilNoResponse={nilNoResponse}
          hotCount={hotCount}
          staleCount={staleCount}
          handleStartKIT={handleStartKIT}
          handleChangeCampaign={handleChangeCampaign}
          handleAssignAndStartKIT={handleAssignAndStartKIT}
          updateLead={updateLead}
          handleDelete={handleDelete}
          canDelete={canDelete}
          openEditModal={openEditModal}
          openWhatsApp={openWhatsApp}
          handleLogCall={handleLogCall}
          dueMessages={dueMessages}
          upcomingMessages={upcomingMessages}
          onSendScheduled={handleSendScheduledMessage}
          onSkipScheduled={handleSkipScheduledMessage}
        />
      )}

      {view === 'performance' && (
        <PerformanceView leads={leads} kitActive={kitActive} appointments={appointments} />
      )}

      {view === 'campaigns' && (
        <CampaignBuilderView leads={leads} kitActive={kitActive} appointments={appointments} />
      )}

      {showModal && (
        <LeadModal
          form={form}
          setForm={setForm}
          editingLead={editingLead}
          saving={saving}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          toggleAilment={toggleAilment}
          staffList={staffList}
        />
      )}

      {kitLogModal && (
        <CallLogModal
          patient={{ name: leadName(kitLogModal.lead) }}
          calllead={kitLogModal.lead}
          mode={kitLogModal.mode}
          phone={kitLogModal.lead.number}
          onClose={() => setKitLogModal(null)}
          onSaved={(updates) => handleKitLogSaved(kitLogModal.lead, updates)}
        />
      )}

      {showBulkModal && (
        <KITBulkActivate
          leads={leads}
          campaignPresets={CAMPAIGN_PRESETS}
          onActivate={handleBulkActivate}
          onSendCustom={handleBulkSendCustom}
          onClose={() => setShowBulkModal(false)}
        />
      )}

      {/* WhatsApp config modal — reads configOpen from WhatsAppContext */}
      <WAConfigGate />
    </div>
  );
}

/* Renders WhatsAppConfigModal only when configOpen=true (reads context) */
function WAConfigGate() {
  const { configOpen } = useWhatsApp();
  return configOpen ? <WhatsAppConfigModal /> : null;
}

/* ═══════════════════════════════════════════
   INTELLIGENCE VIEW
═══════════════════════════════════════════ */
function IntelligenceView({
  leads, loading, selectedLead, setSelectedLead,
  activeChips, setActiveChips, toggleChip, activeSources, setActiveSources, toggleSource,
  filteredLeads, totalLeads, kitActive, appointments, willDecide, nilNoResponse,
  hotCount, staleCount,
  handleStartKIT, handleChangeCampaign, handleAssignAndStartKIT, updateLead,
  handleDelete, canDelete, openEditModal, openWhatsApp, handleLogCall,
  dueMessages, upcomingMessages, onSendScheduled, onSkipScheduled,
}) {
  const allLeadsColumns = [
    {
      name: 'Name / Number',
      selector: (r) => leadName(r),
      cell: (row) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AvatarCell lead={row} />
          <span style={{ fontSize: 11, color: '#64748b' }}>{row.number || '—'}{row.relationship && row.relationship !== 'self' ? ` · ${row.relationship}` : ''}</span>
        </div>
      ),
      filterable: true,
    },
    { name: 'Condition', selector: (r) => (r.ailments || []).join(', ') || '—', filterable: true },
    { name: 'Source', selector: 'enquirySource', filterable: true },
    {
      name: 'Status',
      selector: (r) => r.kitActive ? 'KIT Active' : (r.status || '—'),
      cell: (row) => {
        const s = row.kitActive ? 'KIT Active' : (row.status || '—');
        const isNilRow = s === 'Nil / No Response';
        const isStaleRow = !row.kitActive && isStale(row);
        return (
          <span style={{ color: statusColor(s), fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
            {isNilRow && <span title="No response — needs attention">⚠️</span>}
            {isStaleRow && !isNilRow && <span title="Not contacted in 7+ days">⏰</span>}
            {s}
          </span>
        );
      },
      filterable: true,
    },
    {
      name: 'Temp',
      selector: 'leadTemperature',
      filterable: true,
      cell: (row) => {
        const t = row.leadTemperature || '';
        if (t.includes('Hot')) return <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 12 }}>🔴 Hot</span>;
        if (t.includes('Warm')) return <span style={{ color: '#d97706', fontWeight: 600, fontSize: 12 }}>🟡 Warm</span>;
        if (t.includes('Cold')) return <span style={{ color: '#0284c7', fontWeight: 500, fontSize: 12 }}>🔵 Cold</span>;
        return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
      },
    },
    {
      name: 'Last Contacted',
      selector: (r) => r.lastContactedDate ? new Date(r.lastContactedDate).getTime() : 0,
      cell: (row) => {
        const d = daysSince(row.lastContactedDate);
        const color = d > 14 ? '#ef4444' : d > 7 ? '#d97706' : '#374151';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ color, fontWeight: d > 7 ? 600 : 400, fontSize: 12 }}>{fmtDate(row.lastContactedDate)}</span>
            {row.contactedPerson && <span style={{ fontSize: 10, color: '#94a3b8' }}>by {row.contactedPerson}</span>}
          </div>
        );
      },
      filterable: false,
    },
    {
      name: 'Follow-up',
      selector: (r) => r.followUpDate ? new Date(r.followUpDate).getTime() : 0,
      cell: (row) => {
        if (!row.followUpDate) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
        const d = new Date(row.followUpDate);
        const overdue = d < new Date();
        const dateStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
        if (overdue) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#ef4444', background: '#fee2e2', borderRadius: 4, padding: '1px 5px', letterSpacing: '0.02em' }}>⚠️ OVERDUE</span>
              <span style={{ fontSize: 10, color: '#dc2626' }}>{dateStr}</span>
            </div>
          );
        }
        return (
          <span style={{ fontSize: 12, color: '#d97706', fontWeight: 500 }}>
            📅 {dateStr}
            {row.followUpHistory?.length > 0 && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>({row.followUpHistory.length}x)</span>}
          </span>
        );
      },
      filterable: false,
    },
    {
      name: 'Campaign',
      selector: (r) => r.kitCampaign || '',
      filterable: false,
      cell: (row) => {
        if (!row.kitActive) {
          const suggested = matchCampaignPreset(row);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }} onClick={e => e.stopPropagation()}>
              <select
                defaultValue=""
                onChange={e => { if (e.target.value) handleAssignAndStartKIT(row, e.target.value); }}
                style={{ fontSize: 10, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 3px', color: '#374151', cursor: 'pointer', maxWidth: 115, background: '#fafafa' }}
              >
                <option value="">🚀 Start KIT…</option>
                <option value={suggested}>{suggested} ✨</option>
                {Object.keys(CAMPAIGN_PRESETS).filter(c => c !== suggested).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          );
        }
        return (
          <div onClick={e => e.stopPropagation()}>
            <select
              value={row.kitCampaign || ''}
              onChange={e => handleChangeCampaign(row, e.target.value)}
              style={{ fontSize: 11, border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 5px', background: '#f0fdfa', color: '#0d9488', fontWeight: 600, cursor: 'pointer', maxWidth: 130 }}
            >
              {Object.keys(CAMPAIGN_PRESETS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        );
      },
    },
    {
      name: 'Msgs',
      selector: (r) => (r.messageHistory || []).length,
      cell: (row) => {
        const cnt = (row.messageHistory || []).length;
        if (!cnt) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
        return (
          <span style={{ fontSize: 12, color: '#25D366', fontWeight: 600 }}>
            💬 {cnt}
            {row.lastMessageDate && <span style={{ fontSize: 10, color: '#94a3b8', display: 'block' }}>{fmtDate(row.lastMessageDate)}</span>}
          </span>
        );
      },
      filterable: false,
    },
    {
      name: 'Actions',
      filterable: false,
      actions: (row) => {
        const acts = [];
        if (row.kitActive) {
          acts.push({ icon: '👁️', label: 'View', desc: 'View lead details', onClick: (r) => setSelectedLead(r) });
        } else if (row.status === 'Appointment Booked') {
          acts.push({ icon: '✅', label: 'Done', desc: 'View booked appointment', onClick: (r) => openEditModal(r) });
        } else {
          acts.push({ icon: '📞', label: 'Call', desc: 'Call lead', onClick: (r) => handleLogCall(r) });
          acts.push({ icon: '🚀', label: 'Start KIT', desc: 'Begin KIT program', onClick: (r) => handleStartKIT(r) });
        }
        acts.push({ icon: '💬', label: 'WhatsApp', desc: 'Send WhatsApp', onClick: (r) => openWhatsApp(r) });
        acts.push({ icon: '✏️', label: 'Edit', desc: 'Edit lead details', onClick: (r) => openEditModal(r) });
        if (canDelete) {
          acts.push({ icon: '🗑️', label: 'Delete', desc: 'Delete this lead', onClick: (r) => handleDelete(r) });
        }
        return acts;
      },
    },
  ];

  const conditionalRowStyles = [
    { when: (r) => r.status === 'Nil / No Response', style: { backgroundColor: '#fff0f0', borderLeft: '4px solid #ef4444' } },
    { when: (r) => isStale(r) && r.status !== 'Nil / No Response', style: { backgroundColor: '#fffbeb', borderLeft: '4px solid #f59e0b' } },
    { when: (r) => r.kitActive, style: { backgroundColor: '#f0fdfa', borderLeft: '4px solid #0d9488' } },
    { when: (r) => r.status === 'Appointment Booked', style: { backgroundColor: '#f0fdf4', borderLeft: '4px solid #16a34a' } },
  ];

  return (
    <div className="kit-intelligence">
      <h1 className="kit-page-title">LEAD INTELLIGENCE DASHBOARD</h1>

      {/* Due / Upcoming KIT Messages */}
      <KITDueDashboard
        dueMessages={dueMessages}
        upcomingMessages={upcomingMessages}
        onSend={onSendScheduled}
        onSkip={onSkipScheduled}
      />

      <div className="kit-intel-layout">
        <div className="kit-intel-main">

          <div className="kit-section-header-row">
            <span className="kit-section-title">ALL ENQUIRIES</span>
            {(activeChips.length > 0 || activeSources.length > 0) && (
              <button className="kit-clear-filters-btn" onClick={() => { setActiveChips([]); setActiveSources([]); }}>
                ✕ Clear filters
              </button>
            )}
          </div>

          {/* ── Status Filter Chips (multi-select) ── */}
          <div className="kit-kpi-chips">
            <button
              className={`kit-chip kit-chip-indigo ${activeChips.length === 0 ? 'active' : ''}`}
              onClick={() => { setActiveChips([]); setActiveSources([]); }}
            >
              <span className="kit-chip-icon">📊</span>
              <span className="kit-chip-num">{totalLeads}</span>
              <span className="kit-chip-lbl">All Leads</span>
            </button>
            <button className={`kit-chip kit-chip-emerald ${activeChips.includes('appointments') ? 'active' : ''}`} onClick={() => toggleChip('appointments')}>
              <span className="kit-chip-icon">✅</span>
              <span className="kit-chip-num">{appointments}</span>
              <span className="kit-chip-lbl">Appointments</span>
            </button>
            <button className={`kit-chip kit-chip-amber ${activeChips.includes('willDecide') ? 'active' : ''}`} onClick={() => toggleChip('willDecide')}>
              <span className="kit-chip-icon">🤔</span>
              <span className="kit-chip-num">{willDecide}</span>
              <span className="kit-chip-lbl">Will Decide</span>
            </button>
            <button className={`kit-chip kit-chip-rose ${activeChips.includes('noResponse') ? 'active' : ''}`} onClick={() => toggleChip('noResponse')}>
              <span className="kit-chip-icon">❌</span>
              <span className="kit-chip-num">{nilNoResponse}</span>
              <span className="kit-chip-lbl">No Response</span>
            </button>
            <button className={`kit-chip kit-chip-teal ${activeChips.includes('kitActive') ? 'active' : ''}`} onClick={() => toggleChip('kitActive')}>
              <span className="kit-chip-icon">🚀</span>
              <span className="kit-chip-num">{kitActive}</span>
              <span className="kit-chip-lbl">KIT Active</span>
            </button>
            <button className={`kit-chip kit-chip-orange ${activeChips.includes('stale') ? 'active' : ''}`} onClick={() => toggleChip('stale')}>
              <span className="kit-chip-icon">⏰</span>
              <span className="kit-chip-num">{staleCount}</span>
              <span className="kit-chip-lbl">Stale</span>
            </button>
            <button className={`kit-chip kit-chip-red ${activeChips.includes('hot') ? 'active' : ''}`} onClick={() => toggleChip('hot')}>
              <span className="kit-chip-icon">🔥</span>
              <span className="kit-chip-num">{hotCount}</span>
              <span className="kit-chip-lbl">Hot Leads</span>
            </button>
          </div>

          {/* ── Source Filter Chips ── */}
          <div className="kit-source-chips">
            <span className="kit-source-chips-label">Source:</span>
            {SOURCES.map((s) => (
              <button
                key={s}
                className={`kit-source-chip ${activeSources.includes(s) ? 'active' : ''}`}
                onClick={() => toggleSource(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {/* ── All Leads ── */}
          <div className="kit-card">
            <div className="kit-card-title-row">
              <span className="kit-card-title">ALL LEADS</span>
              <span className="kit-card-count">{filteredLeads.length} leads</span>
            </div>
            {loading ? <div className="kit-loading">Loading leads...</div> : (
              <CustomDataTable
                columns={allLeadsColumns}
                data={filteredLeads}
                paginationPerPage={1000}
                onRowClick={(row) => setSelectedLead(row)}
                conditionalRowStyles={conditionalRowStyles}
              />
            )}
          </div>

        </div>

        {/* ── RIGHT DETAIL PANEL ── */}
        <div className="kit-detail-panel">
          {selectedLead ? (
            <DetailPanel
              lead={selectedLead}
              onClose={() => setSelectedLead(null)}
              handleStartKIT={handleStartKIT}
              handleChangeCampaign={handleChangeCampaign}
              handleAssignAndStartKIT={handleAssignAndStartKIT}
              updateLead={updateLead}
              openEditModal={openEditModal}
              openWhatsApp={openWhatsApp}
              handleLogCall={handleLogCall}
              appointments={appointments}
              totalLeads={leads.length}
              leads={leads}
            />
          ) : (
            <div className="kit-detail-hint">
              <div className="kit-detail-hint-icon">👤</div>
              <div className="kit-detail-hint-text">Click any lead row to view details</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   🚨 NEEDS ATTENTION SECTION
═══════════════════════════════════════════ */
function NeedsAttentionSection({ needsAttention, nilLeads, staleLeads, openEditModal, handleDelete, canDelete, onLeadClick, handleLogCall }) {
  const [expanded, setExpanded] = useState(false);
  const nilCount = nilLeads.length;
  const staleCount = staleLeads.filter((l) => l.status !== 'Nil / No Response').length;

  const cols = [
    {
      name: 'Name',
      selector: (r) => leadName(r),
      cell: (row) => <AvatarCell lead={row} />,
      filterable: true,
    },
    { name: 'Condition', selector: (r) => (r.ailments || []).join(', ') || '—', filterable: true },
    {
      name: 'Status',
      selector: 'status',
      cell: (row) => <span style={{ color: statusColor(row.status), fontWeight: 600 }}>{row.status || '—'}</span>,
      filterable: true,
    },
    {
      name: 'Days Since Contact',
      selector: (r) => daysSince(r.lastContactedDate || r.lastvisitdate),
      cell: (row) => {
        const d = daysSince(row.lastContactedDate || row.lastvisitdate);
        const txt = d >= 999 ? 'Never contacted' : `${d} days ago`;
        return <span style={{ color: d > 14 ? '#ef4444' : '#d97706', fontWeight: 600 }}>{txt}</span>;
      },
      filterable: false,
    },
    {
      name: '⚠️ Why Not Converting',
      filterable: false,
      cell: (row) => {
        if (row.status === 'Nil / No Response') {
          return <span style={{ color: '#ef4444', fontWeight: 600 }}>❌ Said No / No Response</span>;
        }
        if (!row.lastContactedDate && !row.lastvisitdate) {
          return <span style={{ color: '#d97706', fontWeight: 600 }}>⏰ Never Contacted</span>;
        }
        const d = daysSince(row.lastContactedDate || row.lastvisitdate);
        return <span style={{ color: '#d97706', fontWeight: 600 }}>⏰ Not Followed Up ({d}d ago)</span>;
      },
    },
    {
      name: 'Actions',
      filterable: false,
      actions: (row) => [
        { icon: '📞', label: 'Call Now', desc: 'Call immediately', onClick: (r) => handleLogCall(r) },
        { icon: '✏️', label: 'Edit', desc: 'Edit lead', onClick: (r) => openEditModal(r) },
        ...(canDelete ? [{ icon: '🗑️', label: 'Delete', desc: 'Delete lead', onClick: (r) => handleDelete(r) }] : []),
      ],
    },
  ];

  const attentionRowStyles = [
    { when: (r) => r.status === 'Nil / No Response', style: { backgroundColor: '#fff0f0', borderLeft: '4px solid #ef4444' } },
    { when: (r) => isStale(r) && r.status !== 'Nil / No Response', style: { backgroundColor: '#fffbeb', borderLeft: '4px solid #f59e0b' } },
  ];

  return (
    <div className="kit-attention-card">
      <div className="kit-attention-header" onClick={() => setExpanded(!expanded)}>
        <div className="kit-attention-left">
          <span className="kit-attention-icon">🚨</span>
          <div>
            <div className="kit-attention-title">MANAGEMENT ATTENTION REQUIRED</div>
            <div className="kit-attention-sub">
              These leads are NOT converting to patient enrollment — requires immediate action
            </div>
          </div>
        </div>
        <div className="kit-attention-right">
          <div className="kit-attention-pills">
            {nilCount > 0 && <span className="kit-attn-pill kit-attn-red">❌ {nilCount} No Response</span>}
            {staleCount > 0 && <span className="kit-attn-pill kit-attn-amber">⏰ {staleCount} Stale (7d+)</span>}
            <span className="kit-attn-pill kit-attn-total">{needsAttention.length} Total</span>
          </div>
          <span className="kit-attention-toggle">{expanded ? '▲ Hide' : '▼ Show leads'}</span>
        </div>
      </div>
      {expanded && (
        <div className="kit-attention-body">
          <CustomDataTable
            columns={cols}
            data={needsAttention}
            paginationPerPage={1000}
            conditionalRowStyles={attentionRowStyles}
            onRowClick={(row) => onLeadClick && onLeadClick(row)}
          />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   DETAIL PANEL
═══════════════════════════════════════════ */
function DetailPanel({ lead, onClose, handleStartKIT, handleChangeCampaign, handleAssignAndStartKIT, updateLead, openEditModal, openWhatsApp, handleLogCall, totalLeads, appointments, leads }) {
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const name = leadName(lead);
  const msgHistory = lead.messageHistory || [];
  const followUpDate = lead.followUpDate ? new Date(lead.followUpDate) : null;
  const followUpOverdue = followUpDate && followUpDate < new Date();
  const last3 = [...msgHistory].reverse().slice(0, 3);

  return (
    <div className="kit-detail-inner">
      <button className="kit-detail-close" onClick={onClose}>×</button>

      <div className="kit-detail-avatar-wrap">
        <span className="kit-detail-avatar" style={{ background: avatarColor(name) }}>
          {initials(name)}
        </span>
      </div>
      <div className="kit-detail-name">{name}</div>
      <div className="kit-detail-sub">{(lead.ailments || []).join(' / ') || '—'}</div>
      {lead.enquirySource && (
        <div className="kit-detail-source">
          <span className="kit-source-dot" style={{ background: avatarColor(lead.enquirySource) }} />
          {lead.enquirySource}
        </div>
      )}

      <div className="kit-detail-fields">
        <div className="kit-detail-field">
          <span className="kit-detail-label">Status</span>
          <span style={{ color: statusColor(lead.kitActive ? 'KIT Active' : lead.status), fontWeight: 600 }}>
            ● {lead.kitActive ? 'KIT Active' : (lead.status || '—')}
          </span>
        </div>
        <div className="kit-detail-field">
          <span className="kit-detail-label">Phone</span>
          <a href={`tel:${lead.number}`} className="kit-link">{lead.number || '—'}</a>
        </div>
        {lead.email && (
          <div className="kit-detail-field">
            <span className="kit-detail-label">Email</span>
            <span style={{ fontSize: 11 }}>{lead.email}</span>
          </div>
        )}
        {(lead.city || lead.address) && (
          <div className="kit-detail-field">
            <span className="kit-detail-label">City</span>
            <span>{lead.city || lead.address}</span>
          </div>
        )}
        {lead.relationship && lead.relationship !== 'self' && (
          <div className="kit-detail-field">
            <span className="kit-detail-label">Enquired By</span>
            <span>{lead.name} ({lead.relationship})</span>
          </div>
        )}
        <div className="kit-detail-field">
          <span className="kit-detail-label">Pain Duration</span>
          <span>{lead.painDuration || '—'}</span>
        </div>
        <div className="kit-detail-field">
          <span className="kit-detail-label">Severity</span>
          <span>{lead.painSeverity || '—'}</span>
        </div>
        {(lead.reportsAvailable || []).length > 0 && (
          <div className="kit-detail-field">
            <span className="kit-detail-label">Reports</span>
            <span>{lead.reportsAvailable.join(', ')}</span>
          </div>
        )}
        <div className="kit-detail-field">
          <span className="kit-detail-label">Temperature</span>
          <span style={{
            color: (lead.leadTemperature || '').includes('Hot') ? '#ef4444' : (lead.leadTemperature || '').includes('Warm') ? '#d97706' : '#6b7280',
            fontWeight: 500,
          }}>
            ● {lead.leadTemperature || 'Not Tagged'}
          </span>
        </div>
        <div className="kit-detail-field">
          <span className="kit-detail-label">Last Contacted</span>
          <div>
            <span>{fmtDate(lead.lastContactedDate)}</span>
            {lead.contactedPerson && <span style={{ fontSize: 10, color: '#94a3b8', display: 'block' }}>by {lead.contactedPerson}</span>}
          </div>
        </div>
        {lead.kitActive && kitDay(lead) && (
          <div className="kit-detail-field">
            <span className="kit-detail-label">KIT Day</span>
            <span style={{ color: '#0d9488', fontWeight: 700 }}>Day {kitDay(lead)}</span>
          </div>
        )}
        {lead.enquiryDetails && (
          <div className="kit-detail-field kit-detail-notes">
            <span className="kit-detail-label">Enquiry</span>
            <span className="kit-detail-note-text">{lead.enquiryDetails}</span>
          </div>
        )}
        {lead.comments && (
          <div className="kit-detail-field kit-detail-notes">
            <span className="kit-detail-label">Notes</span>
            <span className="kit-detail-note-text">{lead.comments}</span>
          </div>
        )}
      </div>

      {/* Follow-up alert */}
      {followUpDate && (
        <div style={{
          margin: '0 0 12px',
          padding: '8px 12px',
          borderRadius: 8,
          background: followUpOverdue ? '#fff0f0' : '#f0fdf4',
          border: `1px solid ${followUpOverdue ? '#fca5a5' : '#86efac'}`,
          fontSize: 12,
          color: followUpOverdue ? '#ef4444' : '#16a34a',
          fontWeight: 600,
        }}>
          {followUpOverdue ? '⚠️ Follow-up OVERDUE' : '📅 Next Follow-up'}:&nbsp;
          {followUpDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      )}

      <div className="kit-detail-section-title">QUICK ACTIONS</div>
      <div className="kit-detail-actions">
        <button className="kit-qa-btn kit-qa-blue" onClick={() => handleLogCall(lead)}>
          📞 Call Patient
        </button>
        <button className="kit-qa-btn kit-qa-green" onClick={() => { if (lead.number) openWhatsApp(lead); }}>
          💬 Send WhatsApp
        </button>
        <button className="kit-qa-btn kit-qa-blue" onClick={() => updateLead(lead, { status: 'Appointment Booked' })}>
          📅 Book Appointment
        </button>
        {!lead.kitActive && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <select
              defaultValue=""
              onChange={e => { if (e.target.value) handleAssignAndStartKIT(lead, e.target.value); }}
              style={{ flex: 1, padding: '7px 8px', borderRadius: 8, border: '1.5px solid #0d9488', background: '#f0fdfa', color: '#0d9488', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              <option value="">🚀 Start KIT — pick campaign…</option>
              <option value={matchCampaignPreset(lead)}>{matchCampaignPreset(lead)} ✨ auto-detect</option>
              {Object.keys(CAMPAIGN_PRESETS).filter(c => c !== matchCampaignPreset(lead)).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
        <button className="kit-qa-btn kit-qa-outline" onClick={() => openEditModal(lead)}>
          ✏️ Edit Details
        </button>
        <button className="kit-qa-btn kit-qa-red-outline" onClick={() => updateLead(lead, { status: 'Nil / No Response' })}>
          ✗ Mark Not Interested
        </button>
      </div>

      {/* ── Person-specific journey funnel ── */}
      {(() => {
        const steps = [];
        const enquiryDate = lead.enquiryDate || lead.createdAt;
        steps.push({ icon: '📋', label: 'Enquiry', date: enquiryDate, done: true, color: '#6366f1' });

        const contacted = lead.followUpHistory?.length > 0 || lead.lastContactedDate;
        steps.push({ icon: '📞', label: 'Contacted', date: lead.lastContactedDate, done: !!contacted, color: '#0891b2' });

        steps.push({ icon: '🚀', label: 'KIT Active', date: lead.kitStartDate, done: !!lead.kitActive, color: '#0d9488' });

        const sentCount = (lead.kitSchedule || []).filter(m => m.status === 'sent').length;
        steps.push({ icon: '💬', label: `${sentCount} Msgs Sent`, date: null, done: sentCount > 0, color: '#25D366' });

        const apptBooked = lead.status === 'Appointment Booked';
        steps.push({ icon: '✅', label: 'Appt Booked', date: null, done: apptBooked, color: '#16a34a' });

        return (
          <div style={{ margin: '12px 0', padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              👤 THIS LEAD'S JOURNEY
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
              {steps.map((s, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', fontSize: 13,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: s.done ? s.color : '#e2e8f0',
                      border: s.done ? `2px solid ${s.color}` : '2px solid #e2e8f0',
                      filter: s.done ? 'none' : 'grayscale(1)',
                      opacity: s.done ? 1 : 0.4,
                    }}>{s.icon}</div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: s.done ? '#1e293b' : '#94a3b8', textAlign: 'center', marginTop: 3, lineHeight: 1.2 }}>
                      {s.label}
                    </div>
                    {s.date && s.done && (
                      <div style={{ fontSize: 8, color: '#94a3b8', marginTop: 1 }}>
                        {new Date(s.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </div>
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <div style={{ flex: 0.5, height: 2, marginTop: 13, background: steps[i + 1].done ? '#e2e8f0' : '#f1f5f9', borderTop: `2px ${steps[i].done && steps[i + 1].done ? 'solid #0d9488' : 'dashed #e2e8f0'}` }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })()}

      {/* KIT Campaign Schedule */}
      {lead.kitActive && lead.kitSchedule && lead.kitSchedule.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="kit-detail-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>🚀 KIT CAMPAIGN</span>
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>
              Day {kitDay(lead)} · {lead.kitSchedule.filter(m => m.status === 'sent').length}/{lead.kitSchedule.length} sent
            </span>
          </div>
          {/* Campaign change row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 8px', background: '#f0fdfa', borderRadius: 7, border: '1px solid #a7f3d0' }}>
            <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 600, flexShrink: 0 }}>Campaign:</span>
            <select
              value={lead.kitCampaign || ''}
              onChange={e => handleChangeCampaign(lead, e.target.value)}
              style={{ flex: 1, fontSize: 12, border: '1px solid #6ee7b7', borderRadius: 5, padding: '3px 6px', background: '#fff', color: '#0d9488', fontWeight: 700, cursor: 'pointer' }}
            >
              {Object.keys(CAMPAIGN_PRESETS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span style={{ fontSize: 10, color: '#9ca3af' }}>change rebuilds pending</span>
          </div>
          {/* ── Sent messages ── */}
          {lead.kitSchedule.filter(m => m.status === 'sent').length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ flex: 1, borderTop: '1px dashed #86efac' }} />
                ✅ SENT
                <span style={{ flex: 1, borderTop: '1px dashed #86efac' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {lead.kitSchedule.filter(m => m.status === 'sent').map((msg, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '7px 10px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.3 }}>{msg.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontWeight: 700, fontSize: 12, color: '#14532d' }}>{msg.title}</span>
                        <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, flexShrink: 0 }}>
                          ✓ {msg.sentDate ? new Date(msg.sentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'Sent'}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 3 }}>Day {msg.day} · {msg.type}</div>
                      {(msg.sentText || msg.text) && (
                        <div style={{ fontSize: 11, color: '#374151', background: '#dcfce7', borderRadius: 5, padding: '4px 7px', lineHeight: 1.4 }}>
                          "{(msg.sentText || msg.text).slice(0, 100)}{(msg.sentText || msg.text).length > 100 ? '…' : ''}"
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Due now ── */}
          {lead.kitSchedule.filter(m => m.status === 'pending' && new Date(m.scheduledDate) <= new Date()).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} />
                ⏰ DUE NOW
                <span style={{ flex: 1, borderTop: '1px dashed #fcd34d' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {lead.kitSchedule.filter(m => m.status === 'pending' && new Date(m.scheduledDate) <= new Date()).map((msg, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '7px 10px', borderRadius: 8, background: '#fffbeb', border: '1.5px solid #fcd34d' }}>
                    <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.3 }}>{msg.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontWeight: 700, fontSize: 12, color: '#92400e' }}>{msg.title}</span>
                        <span style={{ fontSize: 10, background: '#fef08a', color: '#854d0e', padding: '1px 6px', borderRadius: 6, fontWeight: 700, flexShrink: 0 }}>⏰ Due</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.4, marginTop: 3 }}>
                        {msg.text.slice(0, 90)}{msg.text.length > 90 ? '…' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Upcoming ── */}
          {lead.kitSchedule.filter(m => m.status === 'pending' && new Date(m.scheduledDate) > new Date()).length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ flex: 1, borderTop: '1px dashed #e2e8f0' }} />
                📅 UPCOMING
                <span style={{ flex: 1, borderTop: '1px dashed #e2e8f0' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {lead.kitSchedule.filter(m => m.status === 'pending' && new Date(m.scheduledDate) > new Date()).map((msg, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 7, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: 16, flexShrink: 0, opacity: 0.7 }}>{msg.icon}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 11, color: '#374151' }}>{msg.title}</span>
                      <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 5 }}>Day {msg.day}</span>
                    </div>
                    <span style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b', padding: '2px 7px', borderRadius: 7, fontWeight: 600, flexShrink: 0 }}>
                      📅 {new Date(msg.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Skipped ── */}
          {lead.kitSchedule.filter(m => m.status === 'skipped').length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic', padding: '3px 0' }}>
                {lead.kitSchedule.filter(m => m.status === 'skipped').length} message(s) skipped
              </div>
            </div>
          )}
        </div>
      )}

      {/* Last 3 messages + expand */}
      <div className="kit-detail-section-title" style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>💬 RECENT MESSAGES {msgHistory.length > 0 ? `(${msgHistory.length} total)` : ''}</span>
        {msgHistory.length > 0 && (
          <button className="kit-expand-history-btn" onClick={() => setShowHistoryModal(true)}>
            View all →
          </button>
        )}
      </div>
      {last3.length > 0 ? (
        <div className="kit-msg-history">
          {last3.map((m, i) => (
            <div key={i} className="kit-msg-history-item" onClick={() => setShowHistoryModal(true)} style={{ cursor: 'pointer' }}>
              <div className="kit-msg-history-meta">
                <span className="kit-msg-history-type">{m.type || 'custom'}</span>
                <span className="kit-msg-history-date">{fmtDate(m.date)}</span>
              </div>
              <div className="kit-msg-history-preview">{(m.text || '').slice(0, 100)}{(m.text || '').length > 100 ? '…' : ''}</div>
            </div>
          ))}
          {msgHistory.length > 3 && (
            <button className="kit-expand-history-btn kit-expand-history-center" onClick={() => setShowHistoryModal(true)}>
              + {msgHistory.length - 3} more — tap to expand full conversation
            </button>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '12px 0', fontStyle: 'italic' }}>
          No messages sent yet. Use Send WhatsApp to start the conversation.
        </div>
      )}

      {showHistoryModal && (
        <HistoryModal lead={lead} onClose={() => setShowHistoryModal(false)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   HISTORY MODAL — full conversation
═══════════════════════════════════════════ */
function HistoryModal({ lead, onClose }) {
  const name = leadName(lead);
  const msgEntries = (lead.messageHistory || []).map(m => ({ ...m, _kind: 'message' }));
  const fupEntries = (lead.followUpHistory || []).map(f => ({ ...f, _kind: 'followup' }));
  const kitEntries = (lead.kitSchedule || [])
    .filter(m => m.status === 'sent' || m.status === 'pending')
    .map(m => ({
      date: m.status === 'sent' ? m.sentDate : m.scheduledDate,
      type: `KIT Day ${m.day} — ${m.title}`,
      text: m.sentText || m.text,
      _kind: 'kit',
      _status: m.status,
      _icon: m.icon,
    }));
  const all = [...msgEntries, ...fupEntries, ...kitEntries].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  return <KitPortal>
    <div className="kit-modal-overlay" onClick={onClose}>
      <div className="kit-history-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kit-modal-header" style={{ background: 'linear-gradient(135deg, #075e54, #128c7e)' }}>
          <span className="kit-modal-title">💬 FULL CONVERSATION — {name}</span>
          <button className="kit-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="kit-history-modal-body">
          {all.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40, fontSize: 14, fontStyle: 'italic' }}>
              No communication history found for this lead.
            </div>
          ) : (
            <div className="kit-history-timeline">
              {all.map((item, i) => (
                <div key={i} className={`kit-history-entry kit-history-${item._kind}`} style={item._kind === 'kit' ? { background: item._status === 'sent' ? '#f0fdf4' : '#fafafa', borderLeft: `3px solid ${item._status === 'sent' ? '#4ade80' : '#94a3b8'}` } : {}}>
                  <div className="kit-history-entry-header">
                    <span className="kit-history-entry-icon">
                      {item._kind === 'kit' ? (item._icon || '🚀') : item._kind === 'message' ? '💬' : '📅'}
                    </span>
                    <span className="kit-history-entry-type">
                      {item.type || (item._kind === 'followup' ? 'Follow-up Note' : 'Message')}
                      {item._kind === 'kit' && (
                        <span style={{ marginLeft: 6, fontSize: 10, background: item._status === 'sent' ? '#dcfce7' : '#f1f5f9', color: item._status === 'sent' ? '#15803d' : '#64748b', padding: '1px 5px', borderRadius: 6 }}>
                          {item._status === 'sent' ? '✓ Sent' : 'Scheduled'}
                        </span>
                      )}
                    </span>
                    <span className="kit-history-entry-date">{fmtDate(item.date)}</span>
                  </div>
                  {item.text && <div className="kit-history-entry-text">{item.text}</div>}
                  {!item.text && item.notes && <div className="kit-history-entry-text" style={{ fontStyle: 'italic', color: '#64748b' }}>{item.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
          {all.length} communication{all.length !== 1 ? 's' : ''} · {msgEntries.length} WhatsApp · {fupEntries.length} follow-up notes · {kitEntries.length} KIT campaign
        </div>
      </div>
    </div>
  </KitPortal>;
}

/* ═══════════════════════════════════════════
   PERFORMANCE VIEW
═══════════════════════════════════════════ */
function PerformanceView({ leads, kitActive, appointments }) {
  const kitLeads = leads.filter((l) => l.kitActive);
  const totalMsgs = leads.reduce((sum, l) => sum + (l.messageHistory || []).length, 0);
  const conversionRate = leads.length > 0 ? Math.round((appointments / leads.length) * 100) : 0;
  const therapyStarted = leads.filter((l) => l.kitActive && l.status === 'Appointment Booked').length;

  /* Group by primary ailment */
  const byAilment = {};
  leads.forEach((l) => {
    const ailment = (l.ailments || [])[0] || 'General';
    if (!byAilment[ailment]) byAilment[ailment] = { leads: 0, appointments: 0 };
    byAilment[ailment].leads++;
    if (l.status === 'Appointment Booked') byAilment[ailment].appointments++;
  });
  const campaigns = Object.entries(byAilment)
    .map(([name, d]) => ({ name, leads: d.leads, appointments: d.appointments, conversion: d.leads ? Math.round((d.appointments / d.leads) * 100) : 0 }))
    .sort((a, b) => b.leads - a.leads)
    .slice(0, 6);

  /* Message type distribution */
  const msgTypeMap = {};
  leads.forEach((l) => {
    (l.messageHistory || []).forEach((m) => {
      const t = m.type || 'custom';
      msgTypeMap[t] = (msgTypeMap[t] || 0) + 1;
    });
  });
  const msgTypes = Object.entries(msgTypeMap)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
  const donutColors = ['#4f46e5', '#16a34a', '#7c3aed', '#d97706'];
  const donutSegs = msgTypes.slice(0, 4).map((t, i) => ({
    label: t.label,
    pct: totalMsgs > 0 ? Math.round((t.count / totalMsgs) * 100) : 0,
    color: donutColors[i],
  }));

  return (
    <div className="kit-performance">
      <h1 className="kit-page-title">KIT PERFORMANCE DASHBOARD</h1>

      <div className="kit-kpi-row">
        <div className="kit-kpi-card kit-kpi-grad-indigo">
          <div className="kit-kpi-icon">👥</div>
          <div className="kit-kpi-body">
            <div className="kit-kpi-label-sm">Active KIT Leads</div>
            <div className="kit-kpi-number">{kitActive}</div>
          </div>
        </div>
        <div className="kit-kpi-card kit-kpi-grad-cyan">
          <div className="kit-kpi-icon">✉️</div>
          <div className="kit-kpi-body">
            <div className="kit-kpi-label-sm">Total Messages Sent</div>
            <div className="kit-kpi-number">{totalMsgs}</div>
          </div>
        </div>
        <div className="kit-kpi-card kit-kpi-grad-violet">
          <div className="kit-kpi-icon">📊</div>
          <div className="kit-kpi-body">
            <div className="kit-kpi-label-sm">Conversion Rate</div>
            <div className="kit-kpi-number">{conversionRate}%</div>
          </div>
        </div>
        <div className="kit-kpi-card kit-kpi-grad-emerald">
          <div className="kit-kpi-icon">📅</div>
          <div className="kit-kpi-body">
            <div className="kit-kpi-label-sm">Appointments Booked</div>
            <div className="kit-kpi-number">{appointments}</div>
          </div>
        </div>
      </div>

      <div className="kit-perf-layout">
        <div className="kit-perf-left">
          <div className="kit-card">
            <div className="kit-card-title">Leads by Condition</div>
            {campaigns.length === 0 ? (
              <div className="kit-empty">No leads with conditions yet.</div>
            ) : (
              <table className="kit-table">
                <thead>
                  <tr><th>Condition</th><th>Leads</th><th>Appointments</th><th>Conversion</th></tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.name}>
                      <td>{c.name}</td>
                      <td>{c.leads}</td>
                      <td>{c.appointments}</td>
                      <td>
                        <div className="kit-conv-cell">
                          <div className="kit-conv-bar-wrap">
                            <div className="kit-conv-bar" style={{ width: `${c.conversion}%` }} />
                          </div>
                          <span className="kit-conv-pct">{c.conversion}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="kit-card">
            <div className="kit-card-title">Leads Currently in KIT</div>
            <table className="kit-table">
              <thead>
                <tr><th>Name</th><th>Condition</th><th>KIT Day</th><th>Status</th></tr>
              </thead>
              <tbody>
                {kitLeads.length === 0 ? (
                  <tr><td colSpan={4} className="kit-empty">No active KIT leads.</td></tr>
                ) : (
                  kitLeads.slice(0, 8).map((lead) => (
                    <tr key={lead._id}>
                      <td>
                        <div className="kit-avatar-cell">
                          <span className="kit-avatar-sm" style={{ background: avatarColor(leadName(lead)) }}>
                            {initials(leadName(lead))}
                          </span>
                          {leadName(lead)}
                        </div>
                      </td>
                      <td>{(lead.ailments || []).slice(0, 1).join('') || '—'}</td>
                      <td style={{ color: '#0d9488', fontWeight: 600 }}>{kitDay(lead) ? `Day ${kitDay(lead)}` : '—'}</td>
                      <td><span className="kit-status-active">Active</span></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="kit-pipeline">
            <div className="kit-pipeline-item kit-pipe-indigo"><span>{leads.length}</span><small>Enquiries</small></div>
            <div className="kit-pipeline-item kit-pipe-teal"><span>{kitActive}</span><small>KIT Started</small></div>
            <div className="kit-pipeline-item kit-pipe-emerald"><span>{appointments}</span><small>Appointments</small></div>
          </div>
        </div>

        <div className="kit-perf-right">
          <div className="kit-card">
            <div className="kit-card-title">Conversion Funnel</div>
            <div className="kit-funnel-rows">
              {[
                { label: 'Enquiries', value: leads.length, pct: 100 },
                { label: 'KIT Started', value: kitActive, pct: leads.length > 0 ? Math.round((kitActive / leads.length) * 100) : 0 },
                { label: 'Appointment Booked', value: appointments, pct: leads.length > 0 ? Math.round((appointments / leads.length) * 100) : 0 },
                { label: 'Therapy Started', value: therapyStarted, pct: leads.length > 0 ? Math.round((therapyStarted / leads.length) * 100) : 0 },
              ].map((row) => (
                <div key={row.label} className="kit-funnel-row">
                  <span className="kit-funnel-row-label">{row.label}</span>
                  <div className="kit-funnel-bar-wrap">
                    <div className="kit-funnel-bar-fill" style={{ width: `${row.pct}%` }} />
                  </div>
                  <span className="kit-funnel-row-val">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="kit-card">
            <div className="kit-card-title">Messages by Type</div>
            {msgTypes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>No messages sent yet.</div>
            ) : (
              msgTypes.slice(0, 6).map((m) => {
                const maxCount = msgTypes[0].count;
                return (
                  <div key={m.label} className="kit-msg-row">
                    <span className="kit-msg-icon">📋</span>
                    <span className="kit-msg-name">{m.label}</span>
                    <div className="kit-msg-bar-wrap">
                      <div className="kit-msg-bar" style={{ width: `${Math.round((m.count / maxCount) * 100)}%` }} />
                    </div>
                    <span className="kit-msg-pct">{m.count}</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="kit-card">
            <div className="kit-card-title">Message Type Distribution</div>
            {donutSegs.length > 0 ? (
              <>
                <div className="kit-msg-types">
                  {msgTypes.slice(0, 4).map((t, i) => (
                    <div key={t.label} className="kit-msg-type-row">
                      <span className="kit-msg-type-icon" style={{ color: donutColors[i] }}>●</span>
                      <span className="kit-msg-type-label">{t.label}</span>
                      <span className="kit-msg-type-count">{t.count}</span>
                    </div>
                  ))}
                </div>
                <DonutChart segments={donutSegs} />
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>No message data yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   DONUT CHART
═══════════════════════════════════════════ */
function DonutChart({ segments }) {
  let cumulative = 0;
  const gradientParts = segments.map((s) => {
    const start = cumulative;
    const end = cumulative + s.pct;
    cumulative = end;
    return `${s.color} ${start}% ${end}%`;
  });

  return (
    <div className="kit-donut-wrap">
      <div className="kit-donut" style={{ background: `conic-gradient(${gradientParts.join(', ')})` }}>
        <div className="kit-donut-hole" />
      </div>
      <div className="kit-donut-legend">
        {segments.map((s, i) => (
          <div key={i} className="kit-legend-row">
            <span className="kit-legend-dot" style={{ background: s.color }} />
            <span className="kit-legend-label">{s.label}</span>
            <span className="kit-legend-pct">{s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   WHATSAPP MODAL
═══════════════════════════════════════════ */
function WhatsAppModal({ lead, onClose, onSend }) {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [text, setText] = useState('');

  const handleTemplateChange = (e) => {
    const id = e.target.value;
    const tpl = WA_TEMPLATES.find((t) => t.id === id);
    setSelectedTemplate(id);
    setText(tpl ? tpl.text.replace('{{name}}', leadName(lead)) : '');
  };

  const name = leadName(lead);
  const finalPreview = text.trim() ? `Namaste ${name} Ji! ${text.trim()}` : '';

  return <KitPortal>
    <div className="kit-modal-overlay" onClick={onClose}>
      <div className="kit-modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
        <div className="kit-modal-header" style={{ background: 'linear-gradient(135deg, #075e54, #128c7e)' }}>
          <span className="kit-modal-title" style={{ color: '#ffffff' }}>
            💬 WhatsApp — {name} ({lead.number || '—'})
          </span>
          <button className="kit-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="kit-modal-body">
          {lead.lastMessageDate && (
            <div style={{ marginBottom: 14, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac', fontSize: 12 }}>
              ✅ Last message sent: <strong>{fmtDate(lead.lastMessageDate)}</strong>
              {lead.lastMessageType && ` (${lead.lastMessageType})`}
              {lead.followUpDate && (
                <> &nbsp;·&nbsp; Next follow-up: <strong>{new Date(lead.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</strong></>
              )}
            </div>
          )}

          <div className="kit-form-group" style={{ marginBottom: 14 }}>
            <label>Message Template</label>
            <select className="kit-input" value={selectedTemplate} onChange={handleTemplateChange}>
              <option value="">— Select a Template —</option>
              {WA_TEMPLATES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="kit-form-group" style={{ marginBottom: 14 }}>
            <label>Message Text (Editable)</label>
            <textarea
              className="kit-textarea"
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your message or select a template above..."
            />
          </div>

          {finalPreview && (
            <div className="kit-wa-preview">
              <div className="kit-wa-preview-label">📱 WHATSAPP PREVIEW</div>
              <div className="kit-wa-preview-bubble">{finalPreview}</div>
            </div>
          )}
        </div>

        <div className="kit-modal-footer">
          <button
            className="kit-btn-primary"
            style={{ background: '#25D366', flex: 1 }}
            onClick={() => onSend(lead, selectedTemplate, text)}
            disabled={!text.trim()}
          >
            📲 Open WhatsApp & Record
          </button>
          <button className="kit-btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  </KitPortal>;
}

/* ═══════════════════════════════════════════
   CAMPAIGN BUILDER VIEW
═══════════════════════════════════════════ */
function CampaignBuilderView({ leads, kitActive, appointments }) {
  const conditions = Object.keys(CAMPAIGN_PRESETS);
  const [condition, setCondition] = useState(conditions[0]);
  const [leadType, setLeadType] = useState('Warm (Interested)');
  const [campaignName, setCampaignName] = useState('Back Pain KIT');
  const [steps, setSteps] = useState(CAMPAIGN_PRESETS[conditions[0]]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(0);
  const [saved, setSaved] = useState(false);

  const handleConditionChange = (c) => {
    setCondition(c);
    setSteps(CAMPAIGN_PRESETS[c] || []);
    setCampaignName(`${c} KIT`);
    setEditingIdx(null);
  };

  const addStep = (afterIdx) => {
    const prevDay = steps[afterIdx]?.day || 0;
    const newStep = {
      day: prevDay + 5,
      type: 'Tip / Advice',
      icon: '💡',
      title: 'New Message',
      text: '',
      delay: 'After 5 Days',
    };
    const newSteps = [...steps];
    newSteps.splice(afterIdx + 1, 0, newStep);
    setSteps(newSteps);
    setEditingIdx(afterIdx + 1);
    setEditDraft({ ...newStep });
  };

  const removeStep = (idx) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditDraft({ ...steps[idx] });
  };

  const saveEdit = () => {
    setSteps((prev) => prev.map((s, i) => i === editingIdx ? editDraft : s));
    setEditingIdx(null);
    setEditDraft(null);
  };

  const handleSaveCampaign = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const typeColors = {
    'Tip / Advice':         { bg: '#d1fae5', border: '#10b981', text: '#065f46' },
    'Authority':            { bg: '#dbeafe', border: '#3b82f6', text: '#1e3a5f' },
    'Success Story':        { bg: '#ede9fe', border: '#8b5cf6', text: '#4c1d95' },
    'Lifestyle / Awareness':{ bg: '#fef3c7', border: '#f59e0b', text: '#78350f' },
    'Video / Doctor':       { bg: '#fce7f3', border: '#ec4899', text: '#831843' },
    'Reminder':             { bg: '#dbeafe', border: '#1a56db', text: '#1e3a5f' },
    'Consultation Prompt':  { bg: '#dcfce7', border: '#16a34a', text: '#14532d' },
  };

  const totalDuration = steps.length > 0 ? Math.max(...steps.map((s) => s.day)) : 0;

  return (
    <div className="kit-campaign-builder">
      {/* Header */}
      <div className="kit-cb-header">
        <h1 className="kit-page-title" style={{ margin: 0 }}>KIT CAMPAIGN BUILDER</h1>
        <button
          className="kit-btn-primary"
          style={{ background: saved ? '#16a34a' : '#1a56db' }}
          onClick={handleSaveCampaign}
        >
          {saved ? '✅ Saved!' : '💾 Save Campaign'}
        </button>
      </div>

      {/* Controls */}
      <div className="kit-cb-controls">
        <div className="kit-cb-control-group">
          <label>Campaign Name:</label>
          <input
            className="kit-input"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            style={{ minWidth: 220 }}
          />
        </div>
        <div className="kit-cb-control-group">
          <label>Condition:</label>
          <select className="kit-input" value={condition} onChange={(e) => handleConditionChange(e.target.value)}>
            {conditions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="kit-cb-control-group">
          <label>Lead Type:</label>
          <select className="kit-input" value={leadType} onChange={(e) => setLeadType(e.target.value)}>
            {TEMP_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Main Layout */}
      <div className="kit-cb-layout">
        {/* Flow Area */}
        <div className="kit-cb-flow-area">
          {/* START node */}
          <div className="kit-cb-start-row">
            <div className="kit-cb-start-chip">▶ START</div>
            <div className="kit-cb-start-label">Incoming {condition} Lead</div>
          </div>
          <div className="kit-cb-arrow-down">↓</div>

          {/* Step cards - horizontal scroll */}
          <div className="kit-cb-steps">
            {steps.map((step, idx) => {
              const tc = typeColors[step.type] || { bg: '#f1f5f9', border: '#94a3b8', text: '#1e293b' };
              const isEditing = editingIdx === idx;
              return (
                <React.Fragment key={idx}>
                  <div className={`kit-cb-card ${isEditing ? 'kit-cb-card-editing' : ''}`} style={{ borderTop: `4px solid ${tc.border}` }}>
                    <div className="kit-cb-card-header" style={{ background: tc.bg, color: tc.text }}>
                      <span>{step.icon} Day {step.day}</span>
                      <span style={{ fontSize: 11 }}>›</span>
                    </div>
                    <div className="kit-cb-card-body">
                      <div className="kit-cb-card-title">{step.title}</div>
                      <div className="kit-cb-card-type" style={{ color: tc.border }}>{step.type}</div>
                      <div className="kit-cb-card-text">{step.text}</div>
                    </div>
                    <div className="kit-cb-card-footer">
                      <button className="kit-cb-edit-btn" onClick={() => { setPreviewIdx(idx); startEdit(idx); }}>
                        ✏️ Edit Message
                      </button>
                      {step.delay && <div className="kit-cb-delay">⏱ {step.delay}</div>}
                    </div>
                    <button className="kit-cb-remove-btn" onClick={() => removeStep(idx)} title="Remove step">×</button>
                  </div>
                  {idx < steps.length - 1 ? (
                    <div className="kit-cb-step-connector">
                      <span className="kit-cb-arrow">→</span>
                    </div>
                  ) : (
                    <div className="kit-cb-step-connector">
                      <button className="kit-cb-add-more" onClick={() => addStep(idx)}>+ Add More Steps</button>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Add step below first card */}
          <div className="kit-cb-add-row">
            {steps.map((_, idx) => (
              <div key={idx} className="kit-cb-add-below">
                <div className="kit-cb-arrow-down" style={{ fontSize: 16 }}>↓</div>
                <button className="kit-cb-add-btn" onClick={() => addStep(idx)}>+ Add Step</button>
              </div>
            ))}
          </div>

          {/* Edit panel */}
          {editingIdx !== null && editDraft && (
            <div className="kit-cb-edit-panel">
              <div className="kit-cb-edit-header">
                ✏️ Editing: Day {editDraft.day} — {editDraft.title}
              </div>
              <div className="kit-cb-edit-body">
                <div className="kit-form-row">
                  <div className="kit-form-group">
                    <label>Day Number</label>
                    <input className="kit-input" type="number" value={editDraft.day} onChange={(e) => setEditDraft((d) => ({ ...d, day: Number(e.target.value) }))} />
                  </div>
                  <div className="kit-form-group">
                    <label>Message Type</label>
                    <select className="kit-input" value={editDraft.type} onChange={(e) => setEditDraft((d) => ({ ...d, type: e.target.value }))}>
                      {MSG_LIBRARY_TYPES.map((t) => <option key={t.label} value={t.label}>{t.icon} {t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="kit-form-group" style={{ marginBottom: 12 }}>
                  <label>Message Title</label>
                  <input className="kit-input" value={editDraft.title} onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))} />
                </div>
                <div className="kit-form-group" style={{ marginBottom: 12 }}>
                  <label>Or pick from library</label>
                  <select className="kit-input" onChange={(e) => {
                    const tpl = WA_TEMPLATES.find((t) => t.id === e.target.value);
                    if (tpl) setEditDraft((d) => ({ ...d, text: tpl.text, type: tpl.type || d.type, title: tpl.label.replace(/^[^ ]+ /, '') }));
                  }}>
                    <option value="">— Select template —</option>
                    {WA_TEMPLATES.filter((t) => t.text).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div className="kit-form-group" style={{ marginBottom: 12 }}>
                  <label>Message Text</label>
                  <textarea className="kit-textarea" rows={4} value={editDraft.text} onChange={(e) => setEditDraft((d) => ({ ...d, text: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="kit-btn-primary" onClick={saveEdit}>✅ Save Step</button>
                  <button className="kit-btn-secondary" onClick={() => { setEditingIdx(null); setEditDraft(null); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Preview panel */}
          {steps[previewIdx] && (
            <div className="kit-cb-preview">
              <div className="kit-cb-preview-label">PREVIEW NEXT MESSAGE</div>
              <div className="kit-cb-preview-title">Day {steps[previewIdx].day} {steps[previewIdx].title}</div>
              <div className="kit-cb-preview-text">{steps[previewIdx].text || 'No message text yet.'}</div>
              <div className="kit-cb-preview-footer">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    className={`kit-cb-preview-dot ${i === previewIdx ? 'active' : ''}`}
                    onClick={() => setPreviewIdx(i)}
                  />
                ))}
              </div>
              <button className="kit-cb-add-more" style={{ marginTop: 12, width: '100%' }} onClick={() => addStep(steps.length - 1)}>
                + Add More Steps
              </button>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="kit-cb-sidebar">
          <div className="kit-cb-stats-card">
            <div className="kit-cb-sidebar-title">KIT STATS</div>
            <div className="kit-cb-stat-row">
              <span className="kit-cb-stat-icon">📋</span>
              <span className="kit-cb-stat-label">Messages</span>
              <span className="kit-cb-stat-val">{steps.length}</span>
            </div>
            <div className="kit-cb-stat-row">
              <span className="kit-cb-stat-icon">⏱</span>
              <span className="kit-cb-stat-label">Duration</span>
              <span className="kit-cb-stat-val">{totalDuration} Days</span>
            </div>
            <div className="kit-cb-stat-row">
              <span className="kit-cb-stat-icon">🚀</span>
              <span className="kit-cb-stat-label">Active KIT</span>
              <span className="kit-cb-stat-val">{kitActive}</span>
            </div>
            <div className="kit-cb-stat-row">
              <span className="kit-cb-stat-icon">📅</span>
              <span className="kit-cb-stat-label">Appointments</span>
              <span className="kit-cb-stat-val">{appointments}</span>
            </div>
            <div className="kit-cb-stat-action" onClick={() => window.alert('Message preview — click Edit on any card to modify.')}>
              📝 Preview a message
            </div>
          </div>

          <div className="kit-cb-library-card">
            <div className="kit-cb-sidebar-title">MESSAGES LIBRARY</div>
            {MSG_LIBRARY_TYPES.map((t) => (
              <div key={t.label} className="kit-cb-lib-item" title={t.desc} onClick={() => {
                const tpl = WA_TEMPLATES.find((w) => w.type === t.label);
                if (tpl) addStep(steps.length - 1);
              }}>
                <span className="kit-cb-lib-icon">{t.icon}</span>
                <span className="kit-cb-lib-label">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LEAD MODAL
═══════════════════════════════════════════ */
function LeadModal({ form, setForm, editingLead, saving, onClose, onSave, toggleAilment, staffList }) {
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const colStyle = { flexDirection: 'column', alignItems: 'flex-start', gap: 6 };

  return <KitPortal>
    <div className="kit-modal-overlay" onClick={onClose}>
      <div className="kit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kit-modal-header">
          <span className="kit-modal-title">
            LEAD CAPTURE — {editingLead ? 'EDIT ENQUIRY' : 'NEW ENQUIRY'}
          </span>
          <button className="kit-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="kit-modal-body">

          {/* ── SECTION 1: Caller Details ── */}
          <div className="kit-form-section-title">Caller / Enquirer Details</div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Caller Name *</label>
              <input className="kit-input" value={form.name} onChange={set('name')} placeholder="Name of person calling" />
            </div>
            <div className="kit-form-group">
              <label>Mobile Number *</label>
              <input className="kit-input" value={form.number} onChange={set('number')} placeholder="10-digit mobile" type="tel" />
            </div>
          </div>
          {/* Email and Address/City removed */}
          <div className="kit-form-group" style={{ marginBottom: 14 }}>
            <label>Relationship to Patient</label>
            <div className="kit-radio-row">
              {['self', 'parent', 'spouse', 'sibling', 'friend'].map((r) => (
                <label key={r} className="kit-radio-label">
                  <input type="radio" name="relationship" value={r} checked={form.relationship === r} onChange={set('relationship')} />
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </label>
              ))}
            </div>
          </div>
          {/* Preferred Contact Method removed */}

          <hr className="kit-divider" />

          {/* ── SECTION 2: Patient Details ── */}
          <div className="kit-form-section-title">Patient Details</div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Patient Name</label>
              <input className="kit-input" value={form.enquiredForName} onChange={set('enquiredForName')} placeholder="Full name of patient (optional)" />
            </div>
          </div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Patient's Direct Mobile (if different)</label>
              <input className="kit-input" value={form.enquiredForNumber || ''} onChange={set('enquiredForNumber')} placeholder="Patient's own number" type="tel" />
            </div>
            <div className="kit-form-group">
              <label>Gender</label>
              <div className="kit-radio-row" style={{ paddingTop: 8 }}>
                {['Male', 'Female', 'Other'].map((g) => (
                  <label key={g} className="kit-radio-label">
                    <input type="radio" name="gender" value={g} checked={form.enquiredForGender === g} onChange={set('enquiredForGender')} />
                    {g}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <hr className="kit-divider" />

          {/* ── SECTION 3: Clinical Details ── */}
          <div className="kit-form-section-title">Clinical Details *</div>
          <div className="kit-form-group" style={{ marginBottom: 14 }}>
            <label>Primary Problem (select all that apply)</label>
            <div className="kit-ailments-grid">
              {AILMENTS.map((a) => (
                <label key={a} className="kit-ailment-item">
                  <input type="checkbox" checked={form.ailments.includes(a)} onChange={() => toggleAilment(a)} />
                  <span>{a}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 12, color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' }}>Other:</label>
              <input className="kit-input" style={{ flex: 1 }} value={form.otherAilment} onChange={set('otherAilment')} placeholder="Specify other condition" />
            </div>
          </div>
          {/* Pain Duration, Pain Severity, Reports Available removed */}
          <div className="kit-form-group" style={{ marginBottom: 14 }}>
            <label>Enquiry Details / Problem Statement</label>
            <textarea className="kit-textarea" rows={2} value={form.enquiryDetails} onChange={set('enquiryDetails')} placeholder="Describe the patient's main complaint, symptoms, history..." />
          </div>

          <hr className="kit-divider" />

          {/* ── SECTION 4: Lead Management ── */}
          <div className="kit-form-section-title">Lead Management</div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Enquiry Source</label>
              <div className="kit-radio-row" style={colStyle}>
                {SOURCES.map((s) => (
                  <label key={s} className="kit-radio-label">
                    <input type="radio" name="source" value={s} checked={form.enquirySource === s} onChange={set('enquirySource')} />
                    {s}
                    {s === 'Instagram' && form.enquirySource === 'Instagram' && (
                      <input className="kit-input kit-input-inline" value={form.instagramHandle} onChange={set('instagramHandle')} placeholder="@handle" />
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="kit-form-group">
              <label>Lead Temperature</label>
              <div className="kit-radio-row" style={colStyle}>
                {TEMP_OPTIONS.map((t) => (
                  <label key={t} className="kit-radio-label">
                    <input type="radio" name="temp" value={t} checked={form.leadTemperature === t} onChange={set('leadTemperature')} />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Enquiry Date</label>
              <input className="kit-input" type="date" value={form.enquiryDate} onChange={set('enquiryDate')} />
            </div>
            <div className="kit-form-group">
              <label>Follow-up Date</label>
              <input className="kit-input" type="date" value={form.followUpDate || ''} onChange={set('followUpDate')} />
            </div>
          </div>
          <div className="kit-form-row">
            <div className="kit-form-group">
              <label>Contacted By (Staff Member)</label>
              <select className="kit-input" value={form.contactedPerson || ''} onChange={set('contactedPerson')}>
                <option value="">-- Select Staff --</option>
                {staffList.map((s) => (
                  <option key={s.username} value={s.username}>{s.firstName ? (s.firstName + ' ' + (s.lastName || '')).trim() : s.username}</option>
                ))}
              </select>
            </div>
            <div className="kit-form-group">
              <label>Referred By</label>
              <input className="kit-input" value={form.referrerName || ''} onChange={set('referrerName')} placeholder="Who referred this lead?" />
            </div>
          </div>

          <hr className="kit-divider" />

          {/* ── SECTION 5: Notes ── */}
          <div className="kit-form-section-title">FollowUp Notes & Remarks</div>
          <div className="kit-form-group">
            <label>FollowUp Call Notes / Remarks</label>
            <textarea className="kit-textarea" rows={3} value={form.comments} onChange={set('comments')} placeholder="Call notes, patient remarks, treatment interest, special considerations..." />
          </div>

        </div>

        <div className="kit-modal-footer">
          <button className="kit-btn-secondary" onClick={() => onSave(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Lead'}
          </button>
          <button className="kit-btn-primary" onClick={() => onSave(false)} disabled={saving}>
            Save + Send Welcome Message
          </button>
          <button className="kit-btn-green-full" onClick={() => onSave(true)} disabled={saving}>
            Convert to Appointment
          </button>
        </div>
      </div>
    </div>
  </KitPortal>;
}
